import { ConversationalMMFullscreenResults } from '@fruitful/conversational-mm/src/fullscreen/ConversationalMMFullscreenResults';
import { CONVERSATIONAL_MM_AGENT } from '@fruitful/llm-service-shared';
import {
    AnalyticsUserEvent,
    CmmArtifactFinalPageExperiment,
    type CmmArtifactFinalPageExperiment_ExperimentParam_Value,
} from '@fruitful/types';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { isNonMobileWeb, isWeb } from 'smoothie';

import { ConvMmAccountForm } from '../../../auth/components/new-member/ConvMmAccountForm';
import { isUserSetupInProgressAtom } from '../../../auth/state/AuthAtoms';
import { StorageKeys } from '../../../common/constants';
import useNewArtifact from '../../../common/hooks/useNewArtifact';
import storage from '../../../common/providers/StorageFacade';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import useExperimentAndLog from '../../../feature-flags/hooks/useExperimentAndLog';
import {
    trackCMMEvent,
    useConversationalMMGuidedTooltips,
} from '../../../llm-service/hooks/useConversationalMMAdapters';
import { useSubmitOnboardingChatResults } from '../../../llm-service/hooks/useSubmitOnboardingChatResults';
import { useUpsertMastraThread } from '../../../llm-service/hooks/useUpsertMastraThread';
import { onboardingChatResultsAtom } from '../../../llm-service/state/OnboardingChatAtoms';
import { captureException, track } from '../../../logger/Logger';
import { useGetActiveGuides } from '../../../onboarding/hooks/useGetActiveGuides';
import { useVisitorId } from '../../../user/providers/VisitorProvider';

/**
 * The chat page (OnboardingChatShell + useMobileWebChatLock) locks the
 * body for keyboard handling on mobile web and paints a green gradient
 * on html/body/root. Because Expo Router uses a default Stack navigator
 * for get-started/, the chat page stays mounted when results is pushed
 * on top — its portaled fixed gradient and viewport-fit=cover persist.
 *
 * This hook resets body/html styles, removes scoped style tags, and
 * restores the viewport. The legacy results path re-applies its own
 * gradient via MobileWebSafariGradientBackground, so clearing first is
 * safe. The new artifact path relies on useClearChatPageGradient (in
 * FullscreenResultsContent) for the Safari bar override.
 */
const useResetChatPageShellBodyLock = () => {
    const isMobileWeb = isWeb && !isNonMobileWeb;

    useLayoutEffect(() => {
        if (!isMobileWeb || typeof document === 'undefined') {
            return;
        }

        const html = document.documentElement;
        const body = document.body;
        const rootNode = document.getElementById('root');

        body.style.position = '';
        body.style.overflow = '';
        body.style.width = '';
        body.style.height = '';
        body.style.top = '';
        body.style.left = '';
        body.style.backgroundColor = '';
        body.style.minHeight = '';

        html.style.backgroundImage = '';
        html.style.background = '';
        html.style.backgroundAttachment = '';
        html.style.backgroundColor = '';
        html.style.minHeight = '';

        if (rootNode != null) {
            rootNode.style.backgroundColor = '';
        }

        for (const id of [
            'chat-page-shell-gradient',
            'mobile-web-safari-gradient-background',
        ]) {
            const node = document.getElementById(id);
            if (node?.parentNode != null) {
                node.parentNode.removeChild(node);
            }
        }

        const viewportMetas = document.querySelectorAll(
            'meta[name="viewport"]',
        );
        viewportMetas.forEach((meta) => {
            meta.setAttribute(
                'content',
                'width=device-width, initial-scale=1, shrink-to-fit=no',
            );
        });
    }, [isMobileWeb]);
};

const ResultsPage = () => {
    useResetChatPageShellBodyLock();
    const { trackViewOnce } = useViewEvents();
    const onboardingChatResults = useAtomValue(onboardingChatResultsAtom);
    const setOnboardingChatResults = useSetAtom(onboardingChatResultsAtom);
    const { isSubmitting, submitOnboardingChatResults } =
        useSubmitOnboardingChatResults();
    const { upsertMastraThread } = useUpsertMastraThread();
    const visitorId = useVisitorId();
    const { hasSeenGuidedTooltips, onGuidedTooltipsSeen } =
        useConversationalMMGuidedTooltips();

    // Stable reference — see the equivalent note in ExpandingGoalTileOverlay.
    const terminalAction = useMemo(
        () => ({
            mode: 'signup' as const,
            isLoading: isSubmitting,
            onPress: submitOnboardingChatResults,
        }),
        [isSubmitting, submitOnboardingChatResults],
    );

    const { data: guides } = useGetActiveGuides({});

    const branch = onboardingChatResults?.rawChatResults?.branch;

    const newArtifactEnabled = useNewArtifact(branch);
    const params = useLocalSearchParams();

    const cmmArtifactFinalPageExperiment = useExperimentAndLog(
        CmmArtifactFinalPageExperiment.name,
    );

    const cmmArtifactFinalPageExperimentValue =
        cmmArtifactFinalPageExperiment.get(
            CmmArtifactFinalPageExperiment.parameters.experiment_param.key,
        ) as CmmArtifactFinalPageExperiment_ExperimentParam_Value | undefined;

    useEffect(() => {
        if (cmmArtifactFinalPageExperimentValue != null) {
            track(
                AnalyticsUserEvent.CMM_RESULTS_ARTIFACT_FINAL_PAGE_SEGMENT,
                {
                    artifactFinalPageSegment:
                        cmmArtifactFinalPageExperimentValue,
                },
                true,
            );
        }
    }, [cmmArtifactFinalPageExperimentValue]);

    const setIsUserSetupInProgress = useSetAtom(isUserSetupInProgressAtom);

    const handleAccountFormSuccess = useCallback(
        (_email: string) => {
            // Best-effort upsert so the Mastra thread includes
            // goalCompletionDates and other client-derived fields.
            const threadId = storage.getString(
                StorageKeys.CONVERSATIONAL_MM_THREAD_ID,
            );
            if (
                threadId != null &&
                threadId !== '' &&
                onboardingChatResults != null
            ) {
                void upsertMastraThread({
                    mastraThreadId: threadId,
                    agentId: CONVERSATIONAL_MM_AGENT.id,
                    visitorId,
                    version: '1.0',
                    results: onboardingChatResults,
                }).catch((error: unknown) => {
                    captureException(
                        new Error(
                            'Error upserting mastra thread on account create',
                        ),
                        { error: error as Error, threadId },
                    );
                });
            }

            setIsUserSetupInProgress(true);
            router.push({
                pathname: '/sign-up/new-member/info',
                params,
            });
        },
        [
            params,
            setIsUserSetupInProgress,
            onboardingChatResults,
            upsertMastraThread,
            visitorId,
        ],
    );

    const renderAccountForm = useCallback(
        () => (
            <ConvMmAccountForm
                onSuccess={handleAccountFormSuccess}
                artifactFinalPageSegment={
                    cmmArtifactFinalPageExperimentValue as CmmArtifactFinalPageExperiment_ExperimentParam_Value
                }
            />
        ),
        [handleAccountFormSuccess, cmmArtifactFinalPageExperimentValue],
    );

    if (onboardingChatResults == null) {
        return <Redirect href={{ pathname: '/sign-up/get-started', params }} />;
    }

    trackViewOnce('SignUp/GetStarted-CMM-Chat/Results');

    return (
        <ConversationalMMFullscreenResults
            results={onboardingChatResults}
            onResultsChange={setOnboardingChatResults}
            terminalAction={terminalAction}
            hasSeenGuidedTooltips={hasSeenGuidedTooltips}
            onGuidedTooltipsSeen={onGuidedTooltipsSeen}
            onTrackEvent={trackCMMEvent}
            useNewArtifact={newArtifactEnabled}
            guides={guides}
            cmmFinalArtifactSegment={cmmArtifactFinalPageExperimentValue}
            renderAccountForm={renderAccountForm}
        />
    );
};

export default ResultsPage;
